import React from 'react';
import DataManager from '../../data/DataManager';
import Transaction from '../../data/models/Transaction';
import User from '../../data/models/User';
import { fetchSubscription, isPortfolioAccessBlocked } from '../../data/SubscriptionUtils';
import PortfolioValueCard from './cards/PortfolioValueCard';
import TopSectorsCard from './cards/TopSectorsCard';
import GeographicExposure from './cards/GeographicExposure';
import BetaGaugeCard from './cards/BetaGaugeCard';
import TopGainersLosersCard from './cards/TopGainersLosersCard';
import TopHoldingsCard from './cards/TopHoldingsCard';
import ReturnVsMarketCard from './cards/ReturnVsMarketCard';
import ForecastCard from './cards/ForecastCard';
import AnnualDividendCard from './cards/AnnualDividendCard';
import AssetAllocationCard from './cards/AssetAllocationCard';
import AnalystRatingsCard from './cards/AnalystRatingsCard';
import AllHoldingsCard from './cards/AllHoldingsCard';

class PortfolioView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
      subscriptionExpired: false
    };
  }

  componentDidMount() {
    const dataManager = DataManager.getInstance();
    const users = User.findAll();
    const user = users.length ? users[0] : null;

    const subscriptionPromise = user
      ? fetchSubscription(user).catch(() => null)
      : Promise.resolve(null);

    subscriptionPromise.then((subscription) => {
      if (isPortfolioAccessBlocked(subscription)) {
        this.setState({
          loaded: true,
          subscriptionExpired: true
        });
        return;
      }

      Transaction.fetch()
        .then(async (transactions) => {
          if (!transactions.length) {
            return;
          }
          const tickers = [...new Set(transactions.map((txn) => txn.stockTicker).filter(Boolean))];
          await dataManager.fetchStockPrices(tickers);
          const holdings = dataManager.portfolio.getHoldingsForChart();
          await Promise.all([
            dataManager.fetchPortfolioChart(holdings),
            dataManager.fetchMarketBenchmark()
          ]);
        })
        .then(() => {
          this.setState({
            loaded: true,
            subscriptionExpired: false
          });
        });
    });
  }

  render() {
    if (!this.state.loaded) {
      return (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (this.state.subscriptionExpired) {
      return (
        <div className="portfolio-empty-state">
          <p className="portfolio-empty-state-message">
            Subscription expired. Please renew your subscription.
          </p>
        </div>
      );
    }

    const transactions = Transaction.findAll();
    if (!transactions.length) {
      return (
        <div className="portfolio-empty-state">
          <p className="portfolio-empty-state-message">
            Add or import transactions to get insight into your portfolio, or start by browsing Market data.
          </p>
        </div>
      );
    }

    const { portfolio } = DataManager.getInstance();
    const annualDividend = portfolio.getAnnualDividend();
    const periodReturns = portfolio.getPeriodReturns();
    return (
      <span>
        <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-2"></div>
        <div className="row dashboard-card-row mb-4">
          <PortfolioValueCard
            portfolioValue={portfolio.getPortfolioValue()}
            chartData={portfolio.getChartData()}
            dayChange={portfolio.getDayChange()}
          />
          <div className="col-12 col-xl-3 dashboard-metrics-stack">
            <ForecastCard
              oneYearForecast={portfolio.getOneYearForecast()}
              portfolioValue={portfolio.getPortfolioValue()}
            />
            <AnnualDividendCard
              amount={annualDividend?.amount}
              percentOfPortfolio={annualDividend?.percentOfPortfolio}
            />
            <ReturnVsMarketCard
              portfolioReturnPct={periodReturns?.portfolioReturnPct}
              marketReturnPct={periodReturns?.marketReturnPct}
            />
          </div>
          <BetaGaugeCard value={portfolio.getBeta()} />
        </div>
        <div className="row dashboard-card-row mb-4">
          <TopSectorsCard chartData={portfolio.getTopSectors()} />
          <TopGainersLosersCard title="Top Gainers" data={portfolio.getTopGainers()} />
          <TopGainersLosersCard title="Top Losers" data={portfolio.getTopLosers()} />
          <TopHoldingsCard items={portfolio.getTopHoldings()} />
        </div>
        <div className="row dashboard-card-row mb-4">
          <GeographicExposure countries={portfolio.getCountryExposure()} />
          <AnalystRatingsCard chartData={portfolio.getAnalystRatings()} />
          <AssetAllocationCard chartData={portfolio.getAssetAllocation()} />
        </div>
        <div className="row dashboard-card-row mb-4">
          <AllHoldingsCard data={portfolio.getHoldings()} />
        </div>
      </span>
    );
  }
}

export default PortfolioView;
